import {first} from 'lodash';
import {Component, OnInit, Inject, Input} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {Brands, Copy, CtaType, Downgrade, FeatureCopy, pageTypes, PlanSummary, UIFeatureFlag, UIFeatureFlagsEnum} from '@ox/typings';
import {CopyFormattingService} from '@webu/data';
import {combineLatest, map, Subject, take, tap} from 'rxjs';
import {AutoDestroy, BRAND, buildTrackingString, LinkPipe, WINDOW} from '@ox/utils';
import {Router} from '@angular/router';
import {APP_BASE_HREF} from '@angular/common';
import {FeatureFlagService, OmnitureModalNames, selectFeatureFlag} from '@ox/data';
import {Store} from '@ngrx/store';
import {OX_KEYS} from '@ox/config';

@Component({
  selector: 'webu-downgrade-modal',
  templateUrl: './downgrade-modal.component.html'
})
export class DowngradeModalComponent implements OnInit {
  @Input() planCodes: string[];
  @Input() planSummary: PlanSummary;
  downgradeHasShown = false;
  keepYourCurrentButtonText: string;
  continueWithButtonText:string;
  disableClickTracking?:string; 

  interimCatalogFlag$ = this.featureFlagService.featureFlags$.pipe(
    map(flags => flags.UI[UIFeatureFlagsEnum.OC_INTERIM_CATALOG])
  );

  trackingString = (cta: CtaType, args: string[]) => buildTrackingString(pageTypes.checkout, cta, args, OmnitureModalNames.DOWNGRADE_METHOD);
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  isLoading = false;
  downgrade?: Downgrade;
  lostFeatures: (FeatureCopy| string)[] = [];

  copy$ = this.copyService.copy$
    .pipe(take(1));

  combined$ = combineLatest([this.copy$, this.interimCatalogFlag$]).pipe(
    tap(([copy, interimCatalog]) => this.handleCombined([copy, interimCatalog]))
  );
  constructor(
    private copyService: CopyFormattingService,
    public bsModalRef: BsModalRef,
    private router: Router,
    private store: Store,
    @Inject(BRAND) public brand: Brands,
    @Inject(APP_BASE_HREF) public baseHref: string,
    private link: LinkPipe,
    @Inject(WINDOW) private window: Window,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.downgrade = first(this.planSummary.downgrade);
    this.store.select(selectFeatureFlag({id: UIFeatureFlag.CHECKOUT, group: 'UI'})).pipe(
      take(1),
      tap((flag)=>{
        return flag==='false' ? this.disableClickTracking = 'disable-click-tracking' : this.disableClickTracking = undefined;
      })
    ).subscribe();
  }

  handleCombined([copy, interimCatalog]: [Copy | undefined, boolean]) {
    if (copy) {
      if (interimCatalog) {
        this.getLostFeaturesName();
      } else {
        this.getLostFeatures(copy);
      }
      this.isLoading = false;
    } else {
      this.handleError('Copy is undefined');
    }
  }

  /** Finds every unique lost feature and grabs its copy from the Copy API response */
  getLostFeatures(copy: Copy) {
    this.lostFeatures = [];
    this.planSummary.downgrade?.forEach((downgrade) => {
      const product = copy.products?.find((product) => product.code === 'Premium');
      downgrade.lostFeatures.forEach((feature) => {
        const lostFeatureCopy = product?.features.find(f => f.code === feature);
        if (lostFeatureCopy && !this.lostFeatures.some(lostFeature => typeof lostFeature ==='object' && lostFeature.code === feature)) {
          this.lostFeatures.push(lostFeatureCopy);
        }
      });
    });
  }

  getLostFeaturesName() {
    this.lostFeatures = this.planSummary.downgrade?.flatMap(downgrade => downgrade.lostFeatures) || [];
  }

  isFeatureObj(feature: string | FeatureCopy): feature is FeatureCopy {
    return (feature as FeatureCopy).name !== undefined;
  }

  getGenericCopy$ = (code: string, varMap?: Record<string, string>) => 
    this.copyService.getGenericCopy$(code, varMap);

  onContinue() {
    this.bsModalRef.hide();
    this.downgradeHasShown = true;
    // TODO: Add any state that is needed
    this.router.navigate(['./shop/checkout'], {
      queryParams: {
       downgradeHasShown: this.downgradeHasShown,
       prevPage: this.router.url
      }
    });
  }

  onKeepPlanOrClose(){
    if(this.router.url.includes('/shop/checkout')){
      const urlKeys = Object.fromEntries(Object.entries(OX_KEYS.PDP.URL).map(a=> a.reverse()));
      const planCode = urlKeys[this.planCodes[0]];
      if (this.planCodes[0]) {
        this.window.location.href = this.link.transform(`/ownercenter/onstar/shop/${planCode}`);
      }
    }
    else{
      this.bsModalRef.hide();
    }
  }

  handleError(err: any) {
    // TODO: Add error state
    console.error(err);
    this.isLoading = false;
  }
}
